import React, { useEffect, useMemo, useReducer } from 'react';
import PropTypes from 'prop-types';
import StravaAlert from 'components/StravaAlert';
import { FOLLOWING_STATUS } from 'constants/follows/following-constants';
import FriendList from '../components/shared/FriendList';
import FriendListItem from '../components/shared/FriendListItem';
import FriendsContext from '../state/FriendsContext';
import { reducer } from '../state/friendsReducer';
import { UPDATE_FRIENDS } from '../state/friendsActions';
import { SUGGESTED_FRIENDS } from '../utils/friendsConstants';

import styles from './SuggestedFriendList.scss';

const SuggestedFriendList = ({
  track,
  currentAthlete,
  suggestions,
  onFollowingFriendsChange
}) => {
  const [state, dispatch] = useReducer(reducer, {
    currentAthlete,
    source: SUGGESTED_FRIENDS,
    friends: {},
    followedFriends: {},
    onFollowingFriendsChange,
    alert: null
  });
  const { alert, friends } = state;

  const providerState = useMemo(() => ({ state, dispatch, track }), [
    state,
    track
  ]);

  // process and save suggested friends
  useEffect(() => {
    const processedFriends = suggestions.map((friend) => ({
      id: friend.id,
      picture: friend.avatar_url,
      followingStatus: friend.following_status,
      isFollowable:
        friend.following_status === FOLLOWING_STATUS.canFollow ||
        friend.following_status === FOLLOWING_STATUS.canRequest,
      approveFollowers: friend.approve_followers,
      badge: friend.badge,
      displayName: friend.title,
      subtitle: friend.subtitle
    }));
    dispatch({
      type: UPDATE_FRIENDS,
      payload: {
        friends: Object.fromEntries(
          processedFriends.map((friend) => [friend.id, friend])
        )
      }
    });

    track({
      action: 'screen_enter',
      properties: {
        total_num_suggestions: suggestions.length
      }
    });
  }, [suggestions, track]);

  return (
    <FriendsContext.Provider value={providerState}>
      {alert && (
        <StravaAlert severity={alert.severity} className={styles.noBackground}>
          {alert.message}
        </StravaAlert>
      )}
      <FriendList>
        {Object.values(friends).map((friend) => (
          <FriendListItem id={friend.id} key={friend.id} />
        ))}
      </FriendList>
    </FriendsContext.Provider>
  );
};

SuggestedFriendList.defaultProps = {
  onFollowingFriendsChange: () => {}
};

SuggestedFriendList.propTypes = {
  track: PropTypes.func.isRequired,
  currentAthlete: PropTypes.shape({
    id: PropTypes.number
  }).isRequired,
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      subtitle: PropTypes.string,
      avatar_url: PropTypes.string,
      action: PropTypes.shape({
        initial: PropTypes.shape({
          text: PropTypes.string
        }),
        completed: PropTypes.shape({
          text: PropTypes.string
        })
      })
    })
  ).isRequired,
  onFollowingFriendsChange: PropTypes.func
};

export default SuggestedFriendList;
